const ALERT_SOUND_MAP = {
  zone_approach: "/sounds/approach.mp3",
  spike_detected: "/sounds/spike.mp3",
  zone_failed: "/sounds/fail.mp3",
  market_in_zone: "/sounds/zone.mp3",
};

class SoundManager {
  constructor() {
    this.unlocked = false;
    this.queue = [];
    this.audioMap = new Map();
    this.brokenAudio = new Set();
    this.audioContext = null;
    this.boundUnlock = this.unlock.bind(this);
    this.boundVisibility = this.handleVisibilityChange.bind(this);
  }

  initialize() {
    if (this.initialized || typeof window === "undefined") {
      return;
    }

    this.initialized = true;
    console.debug("[sound] initialize");

    Object.entries(ALERT_SOUND_MAP).forEach(([eventType, src]) => {
      const audio = new Audio(src);
      audio.preload = "auto";
      audio.volume = 1;
      audio.addEventListener("error", () => {
        this.brokenAudio.add(eventType);
        console.debug("[sound] audio error", { eventType, src });
      });
      this.audioMap.set(eventType, audio);
    });

    window.addEventListener("pointerdown", this.boundUnlock, { passive: true });
    window.addEventListener("keydown", this.boundUnlock, { passive: true });
    document.addEventListener("visibilitychange", this.boundVisibility);
  }

  destroy() {
    if (!this.initialized || typeof window === "undefined") {
      return;
    }

    window.removeEventListener("pointerdown", this.boundUnlock);
    window.removeEventListener("keydown", this.boundUnlock);
    document.removeEventListener("visibilitychange", this.boundVisibility);
    this.queue = [];
    this.initialized = false;
    console.debug("[sound] destroy");
  }

  unlock() {
    if (this.unlocked) {
      return;
    }

    this.unlocked = true;
    console.debug("[sound] unlocked");
    this.ensureAudioContext();

    this.audioMap.forEach((audio) => {
      audio.muted = true;
      audio.play().catch(() => {}).finally(() => {
        audio.pause();
        audio.currentTime = 0;
        audio.muted = false;
      });
    });

    this.flushQueue();
  }

  play(eventType) {
    if (document.visibilityState !== "visible") {
      console.debug("[sound] queue while hidden", { eventType });
      this.queue.push(eventType);
      return;
    }

    if (!this.unlocked) {
      console.debug("[sound] queue while locked", { eventType });
      this.queue.push(eventType);
      return;
    }

    const audio = this.audioMap.get(eventType);

    if (!audio) {
      console.debug("[sound] missing audio asset, using fallback tone", { eventType });
      this.playFallbackTone(eventType);
      return;
    }

    audio.currentTime = 0;
    console.debug("[sound] play attempt", {
      eventType,
      src: audio.currentSrc || audio.src,
    });
    audio
      .play()
      .then(() => {
        console.debug("[sound] play success", { eventType });
      })
      .catch((error) => {
        this.brokenAudio.add(eventType);
        console.debug("[sound] play failed, using fallback tone", {
          eventType,
          message: error?.message,
          name: error?.name,
        });
        this.playFallbackTone(eventType);
      });
  }

  ensureAudioContext() {
    if (typeof window === "undefined") {
      return null;
    }

    if (!this.audioContext) {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;

      if (!AudioContextClass) {
        return null;
      }

      this.audioContext = new AudioContextClass();
    }

    if (this.audioContext.state === "suspended") {
      this.audioContext.resume().catch(() => {});
    }

    return this.audioContext;
  }

  playFallbackTone(eventType) {
    if (this.brokenAudio.has(eventType) === false && this.audioMap.has(eventType)) {
      return;
    }

    const context = this.ensureAudioContext();

    if (!context) {
      console.debug("[sound] fallback tone unavailable: no audio context", { eventType });
      return;
    }

    console.debug("[sound] fallback tone", { eventType });

    const config = {
      zone_approach: { frequency: 660, duration: 0.22, gain: 0.28 },
      spike_detected: { frequency: 880, duration: 0.28, gain: 0.32 },
      zone_failed: { frequency: 330, duration: 0.34, gain: 0.3 },
      market_in_zone: { frequency: 540, duration: 0.42, gain: 0.34 },
    }[eventType] || { frequency: 520, duration: 0.24, gain: 0.26 };

    const oscillator = context.createOscillator();
    const gainNode = context.createGain();
    const startAt = context.currentTime;
    const endAt = startAt + config.duration;

    oscillator.type = "sine";
    oscillator.frequency.setValueAtTime(config.frequency, startAt);
    gainNode.gain.setValueAtTime(0.0001, startAt);
    gainNode.gain.exponentialRampToValueAtTime(config.gain, startAt + 0.02);
    gainNode.gain.exponentialRampToValueAtTime(0.0001, endAt);

    oscillator.connect(gainNode);
    gainNode.connect(context.destination);
    oscillator.start(startAt);
    oscillator.stop(endAt);
  }

  handleVisibilityChange() {
    if (document.visibilityState !== "visible") {
      return;
    }

    this.flushQueue();
  }

  flushQueue() {
    if (!this.queue.length) {
      return;
    }

    if (!this.unlocked || document.visibilityState !== "visible") {
      return;
    }

    const nextEventType = this.queue.shift();
    console.debug("[sound] flush queued event", { eventType: nextEventType });
    this.play(nextEventType);

    if (this.queue.length) {
      window.setTimeout(() => this.flushQueue(), 350);
    }
  }
}

const soundManager = new SoundManager();

export default soundManager;
